import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
	User,
	UserAndEmail,
	Client,
	StrideActivityUuids,
	Course,
} from '@mira-avant/mtlibs';
import { UserCreatePayload, UserUpdatePayload } from '@mira-avant/mtlibs';
import { CognitoService } from './cognito.service';
import { ClientService } from './client.service';
import { ApiService } from './api.service';
import { NavigationService } from './navigation.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private readonly BASE_PATH = 'users';

	constructor(
		private api: ApiService,
		private cognitoService: CognitoService,
		private clientService: ClientService,
		private nav: NavigationService,
		private message: NzMessageService
	) {}

	// UUID conversion methods
	public async convertUuidToCognitoUuid$(uuid: string): Promise<string> {
		const response = await firstValueFrom(
			this.api.get<{ cognito_uuid: string }>(
				`${this.BASE_PATH}/convert-uuid-to-cognito-uuid/${uuid}`
			)
		);
		return response.cognito_uuid;
	}

	public async convertCognitoUuidToUuid$(cognitoUuid: string): Promise<string> {
		const response = await firstValueFrom(
			this.api.get<{ uuid: string }>(
				`${this.BASE_PATH}/convert-cognito-uuid-to-uuid/${cognitoUuid}`
			)
		);
		return response.uuid;
	}

	// Get a single user by UUID with optional sections
	public async get$(
		cognitoUuid: string,
		includeSections: boolean = false,
		includeLocations: boolean = false,
		includeClients: boolean = false
	): Promise<User> {
		const params = {
			includeSections: includeSections.toString(),
			includeLocations: includeLocations.toString(),
			includeClients: includeClients.toString(),
		};

		return firstValueFrom(
			this.api.get<User>(`${this.BASE_PATH}/by-cognito-uuid/${cognitoUuid}`, { params })
		);
	}

	// Create a new user
	public async create$(userData: UserCreatePayload): Promise<User> {
		return firstValueFrom(this.api.post<User>(this.BASE_PATH, userData));
	}

	// Update a user
	public async update$(uuid: string, userData: UserUpdatePayload): Promise<User> {
		return firstValueFrom(this.api.put<User>(`${this.BASE_PATH}/${uuid}`, userData));
	}

	// Delete a user
	public async delete$(uuid: string): Promise<void> {
		return firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
	}

	// Upsert a user
	public async upsert$(
		uuid: string,
		userData: UserCreatePayload & UserUpdatePayload
	): Promise<User> {
		try {
			await this.get$(uuid);
			return this.update$(uuid, userData);
		} catch (error) {
			if (error instanceof HttpErrorResponse && error.status === 404) {
				return this.create$(userData);
			}
			throw error;
		}
	}

	/**
	 * Fetches users and their associated sections for a list of email addresses
	 */
	public async getUsersAndSections(emails: string[]): Promise<UserAndEmail[]> {
		if (!emails?.length) {
			throw new Error('Valid array of emails is required');
		}

		const emailUuids = await this.cognitoService.getUuidsByEmails(emails);

		if (!emailUuids.length) {
			return [];
		}

		interface BulkUsersResponse {
			success: boolean;
			data: User[];
		}

		const response = await firstValueFrom(
			this.api.post<BulkUsersResponse>(`${this.BASE_PATH}/bulk-get-with-sections`, {
				uuids: emailUuids.map(eu => eu.uuid),
			})
		);

		if (response.success) {
			return response.data.map(user => {
				const emailUuid = emailUuids.find(eu => eu.uuid === user.cognito_uuid);
				return {
					user,
					email: emailUuid!.email,
				};
			});
		}

		throw new Error('Failed to fetch users and sections');
	}

	/**
	 * Removes all sections for the specified emails
	 */
	public async removeUsersSections(emails: string[]): Promise<void> {
		if (!emails?.length) {
			throw new Error('Valid array of emails is required');
		}

		const emailUuids = await this.cognitoService.getUuidsByEmails(emails);

		if (!emailUuids.length) {
			return;
		}

		interface BulkRemoveResponse {
			success: boolean;
		}

		const response = await firstValueFrom(
			this.api.post<BulkRemoveResponse>(`${this.BASE_PATH}/bulk-remove-sections`, {
				uuids: emailUuids.map(eu => eu.uuid),
			})
		);

		if (!response.success) {
			throw new Error('Failed to remove user sections');
		}
	}

	public async getTeachers$(): Promise<User[]> {
		const client: Client = await firstValueFrom(this.clientService.selectedClient$);

		return firstValueFrom(this.api.get<User[]>(`${this.BASE_PATH}/teachers/${client.uuid}`));
	}

	/**
	 * If enrolled in a Stride course, redirects to a random activity of that course.
	 *
	 * @public
	 * @async
	 * @param {Course} course
	 * @returns {Promise<boolean>}
	 */
	public async redirectToStrideCourse$(course: Course): Promise<boolean> {
		const client: Client | undefined = await firstValueFrom(this.clientService.selectedClient$);

		if (!client) {
			console.warn('No client selected, cannot identify Stride presence.');
			return false;
		}

		const matrixParams = await firstValueFrom(this.nav.matrixParams$);

		if(!matrixParams['sectionUuid']) {
			console.warn('No sectionUuid in matrix params, cannot identify Stride presence.');
			return false;
		}

		try {
			const activityUuids = await firstValueFrom(
				this.api.get<StrideActivityUuids>(
					`${this.BASE_PATH}/get-stride-activity/${client.uuid}/${course.uuid}/${matrixParams['sectionUuid']}`
				)
			);

			if (activityUuids) {
				await this.nav.navigateWithMatrix$(
					['/', 'my', 'journey'],
					{
						sectionUuid: activityUuids.sectionUuid,
						courseUuid: activityUuids.courseUuid,
						locationUuid: activityUuids.locationUuid,
						unitUuid: activityUuids.unitUuid,
						lessonUuid: activityUuids.lessonUuid,
						activityUuid: activityUuids.activityUuid,
					},
					false
				);

				return true;
			}
		} catch (error) {
			console.error('No Stride activity found or error fetching activity:', error);

			this.message.error(
				'Unable to access Stride activity at this time. Please try again later.'
			);
		}

		return false;
	}
}
